import { motion } from "framer-motion";
import { ShieldCheck, Award, Users, Wrench, Check } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { Counter } from "../../components/public/Counter";
import { CtaBand } from "../../components/public/Shared";
import { COMPANY } from "../../lib/api";

const VALUES = [
  { icon: ShieldCheck, title: "Licensed & insured", text: "Fully licensed roof plumbing and comprehensive insurance — peace of mind on every job." },
  { icon: Award, title: "Quality workmanship", text: "We don't do band-aid fixes. Every job is done to spec, the way it should be done." },
  { icon: Users, title: "Trusted across WA", text: "Homeowners, property managers and commercial clients rely on us, job after job." },
  { icon: Wrench, title: "The right tools, the right crew", text: "A qualified team with the gear and know-how for jobs big and small." },
];

export default function About() {
  const years = new Date().getFullYear() - COMPANY.since;
  return (
    <>
      <Seo title="About Us" description="Meet West Face Roofing — led by Jake Ucich, a qualified roof plumber & project manager. Building roofs across Perth since 2010. Licensed, insured & trusted." path="/about" />
      <section className="pt-32 md:pt-44 pb-16 bg-[#1A2421] text-white relative overflow-hidden">
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-4">About West Face</p>
            <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-none text-balance">A decade of doing roofs right.</h1>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-lg">
              Since {COMPANY.since}, West Face Roofing has been the name Perth trusts for honest advice, fair prices and quality workmanship — on family homes and major commercial assets alike.
            </p>
          </div>
          <div className="relative h-[420px] rounded-3xl overflow-hidden border border-white/10">
            <img src="https://images.unsplash.com/photo-1635249578213-68b0aa67fdf7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="West Face Roofing crew" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#224733] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-3 gap-8 text-center">
          <div><div className="font-display font-black text-5xl text-[#62B848]"><Counter to={years} suffix="+" /></div><div className="mt-2 text-white/70">Years in business</div></div>
          <div><div className="font-display font-black text-5xl text-[#62B848]"><Counter to={2400} suffix="+" /></div><div className="mt-2 text-white/70">Roofs completed</div></div>
          <div><div className="font-display font-black text-5xl text-[#62B848]"><Counter to={100} suffix="%" /></div><div className="mt-2 text-white/70">Licensed &amp; insured</div></div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative h-[480px] rounded-3xl overflow-hidden order-2 lg:order-1">
            <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_westface-preview/artifacts/k8wmmsmc_1713601640380.jpg" alt="Jake Ucich, Director of West Face Roofing" className="w-full h-full object-cover object-top" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3">Meet the director</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-tight mb-5">Jake Ucich</h2>
            <p className="text-[#4A5550] text-lg leading-relaxed mb-5">
              Jake is a qualified roof plumber and project manager who leads every West Face job from quote to completion. He's built his reputation on showing up when he says he will, quoting fairly, and leaving every site tidy.
            </p>
            <ul className="space-y-3">
              {["Qualified roof plumber & project manager", "Hands-on on every major project", "Specialist in commercial remedial roofing", "Straight-talking, no-surprises quoting"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#1A2421]"><Check size={18} className="text-[#62B848] mt-1 shrink-0" /> {p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-none mb-12 max-w-xl">Why Perth chooses West Face.</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                className="bg-[#F8F9FA] rounded-3xl border border-black/5 p-7">
                <div className="grid place-items-center w-13 h-13 w-[52px] h-[52px] rounded-2xl bg-[#224733]/8 text-[#224733] mb-5"><v.icon size={24} /></div>
                <h3 className="font-display font-bold text-lg text-[#1A2421] mb-2">{v.title}</h3>
                <p className="text-[#4A5550] text-sm leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </>
  );
}
